import React, { ChangeEvent } from 'react';

import {
  GadgetFormInner,
  GadgetFormInnerContent,
  GadgetFormWrapper,
} from '../../V5GadgetForm/V5GadgetForm';
import { InputWrapper } from '../../V5GadgetForm/styles';
import Typography from 'kingpin/atoms/Typography';
import { Section } from '../../MyAccount/Profile/styles';
import toSentenceCase from '../../../services/toSentenceCase';
import GroupByInput from '../GroupByInput';
import Row from '../../Common/Row';
import Inputs from '../../Inputs';

const Label = ({ children }: { children: string }) => (
  <div style={{ marginBottom: 4 }}>
    <Typography.Body type="Label">{children}</Typography.Body>
  </div>
);

const getServerSortLabel = (
  sortBy: VisualisationDefinitions.ServerSortBy,
  metricName: string | undefined,
  groupBy: string | undefined,
): string => {
  if (sortBy === 'metric (asc)') {
    if (metricName) {
      return `${metricName} (asc)`;
    } else {
      return sortBy;
    }
  } else if (sortBy === 'metric (desc)') {
    if (metricName) {
      return `${metricName} (desc)`;
    } else {
      return sortBy;
    }
  } else if (sortBy === 'alpha (asc)') {
    if (groupBy) {
      return `${groupBy} (asc)`;
    } else {
      return sortBy;
    }
  } else if (sortBy === 'alpha (desc)') {
    if (groupBy) {
      return `${groupBy} (desc)`;
    } else {
      return sortBy;
    }
  } else {
    const error = new Error(`Unknown sortBy: ${sortBy}`);
    error.name = 'getServerSortLabel';
    throw error;
  }
};

const SingleMetricDateMatrixForm = ({
  name,
  onNameChanged,
  description,
  onDescriptionChanged,
  metricId,
  setMetricId,
  interval,
  setInterval,
  clientSortBy,
  setClientSortBy,
  serverSortBy,
  setServerSortBy,
  groupBy,
  setGroupBy,
  limit,
  onLimitChanged,
  comparisonCellFormat,
  setComparisonCellFormat,
  isValid,
  isEditing,
  isLoading,
  metricDisplayName,
  onMetricDisplayNameChanged,
  setMetricDisplayName,
  isSizedToFitOverridden,
  onIsSizedToFitChange,
  isFiscalDisabled,
}: {
  name: string;
  onNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  description: string;
  onDescriptionChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  metricId?: string;
  setMetricId: React.Dispatch<React.SetStateAction<string | undefined>>;
  interval: FleetOps.Interval | FiscalInterval;
  setInterval: React.Dispatch<
    React.SetStateAction<FleetOps.Interval | FiscalInterval>
  >;
  clientSortBy?: VisualisationDefinitions.ClientSortBy;
  setClientSortBy: React.Dispatch<
    React.SetStateAction<VisualisationDefinitions.ClientSortBy | undefined>
  >;
  serverSortBy: VisualisationDefinitions.ServerSortBy;
  setServerSortBy: React.Dispatch<
    React.SetStateAction<VisualisationDefinitions.ServerSortBy>
  >;
  groupBy?: string;
  setGroupBy: React.Dispatch<React.SetStateAction<string | undefined>>;
  limit: string;
  onLimitChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  comparisonCellFormat: MatrixCellType;
  setComparisonCellFormat: React.Dispatch<React.SetStateAction<MatrixCellType>>;

  isValid: boolean;
  isEditing: boolean;
  isLoading: boolean;

  metricDisplayName: string;
  onMetricDisplayNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  setMetricDisplayName: React.Dispatch<React.SetStateAction<string>>;
  isSizedToFitOverridden?: boolean;
  onIsSizedToFitChange: (newValue: boolean) => void;
  isFiscalDisabled?: boolean;
}) => (
  <GadgetFormWrapper>
    <GadgetFormInner isGadgetBuilder={false}>
      <GadgetFormInnerContent isGadgetBuilder={false}>
        <Section>
          <Label>Name</Label>
          <Inputs.TextInput
            placeholder="Name"
            value={name}
            onChange={onNameChanged}
          />
        </Section>
        <Section>
          <Label>Description</Label>
          <Inputs.TextInput
            placeholder="Name"
            value={description}
            onChange={onDescriptionChanged}
          />
        </Section>
        <Section>
          <Label>Metric</Label>
          <Inputs.MetricPicker
            metricId={metricId}
            setMetricId={setMetricId}
            noSpecials
            setMetricDisplayName={setMetricDisplayName}
          />
        </Section>
        <Section>
          <Label>Metric Display Name</Label>
          <Inputs.TextInput
            value={metricDisplayName}
            onChange={onMetricDisplayNameChanged}
            placeholder="Display name"
          />
        </Section>
        <Section>
          <Label>Group By</Label>
          <GroupByInput groupBy={groupBy} setGroupBy={setGroupBy} />
        </Section>
        <Section>
          <Label>Interval</Label>
          <Inputs.Dropdown
            options={[
              'auto',
              'day',
              'week',
              'month',
              'quarter',
              'year',
              'fiscalPeriod',
              'fiscalQuarter',
              'fiscalYear',
            ].map((i) => ({
              label: toSentenceCase(i),
              isDisabled:
                ['fiscalPeriod', 'fiscalQuarter', 'fiscalYear'].includes(i) &&
                isFiscalDisabled,
              onSelected: () => {
                setInterval(i as FleetOps.Interval | FiscalInterval);
              },
            }))}
            selectedLabel={toSentenceCase(interval)}
          />
        </Section>
        <Section>
          <Row>
            <div style={{ flex: 1, marginRight: 12 }}>
              <Label>Server Sort Order</Label>
              <Inputs.Dropdown
                options={(
                  [
                    'alpha (asc)',
                    'alpha (desc)',
                    'metric (asc)',
                    'metric (desc)',
                  ] as VisualisationDefinitions.ServerSortBy[]
                ).map((sort) => ({
                  label: toSentenceCase(
                    getServerSortLabel(sort, metricDisplayName, groupBy),
                  ),
                  onSelected: () => {
                    setServerSortBy(sort);
                  },
                }))}
                selectedLabel={toSentenceCase(
                  getServerSortLabel(serverSortBy, metricDisplayName, groupBy),
                )}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Limit</Label>
              <Inputs.TextInput
                value={limit}
                onChange={onLimitChanged}
                type="number"
              />
            </div>
          </Row>
        </Section>
        <Section>
          <Label>Client Sort Order</Label>
          <Inputs.Dropdown
            options={(
              [
                undefined,
                'improvement (asc)',
                'improvement (desc)',
                'improvement % (asc)',
                'improvement % (desc)',
                'change (asc)',
                'change (desc)',
                'change % (asc)',
                'change % (desc)',
              ] as VisualisationDefinitions.ClientSortBy[]
            ).map((sort) => ({
              label: sort ? toSentenceCase(sort) : '',
              onSelected: () => {
                setClientSortBy(sort);
              },
            }))}
            selectedLabel={clientSortBy ? toSentenceCase(clientSortBy) : ''}
          />
        </Section>
        <Section>
          <Label>Comparison Cell Type</Label>
          <Inputs.Dropdown
            selectedLabel={toSentenceCase(comparisonCellFormat)}
            options={['delta', 'percentDelta', 'both'].map((o) => ({
              label: toSentenceCase(o),
              onSelected: () => {
                setComparisonCellFormat(o as MatrixCellType);
              },
            }))}
          />
        </Section>
        <Section>
          <InputWrapper>
            <Label>Is sized to fit</Label>
            <Inputs.Toggle
              value={!!isSizedToFitOverridden}
              onChange={onIsSizedToFitChange}
            />
          </InputWrapper>
        </Section>
      </GadgetFormInnerContent>
    </GadgetFormInner>
  </GadgetFormWrapper>
);

export default SingleMetricDateMatrixForm;
